/**
 * ArtifactDetailPage — /artifacts/:id
 * Demo page: /file-manager (item preview with a details card) on the DetailPage template; tabs (document | raw) live in
 * the URL. Edit and delete are gated by the policies their server routes require: CanEditMemoriesArtifacts (PATCH),
 * CanDeleteMemoriesArtifacts (DELETE). Sub-components live in ./artifact-detail/.
 *
 * The server's read route filters out soft-deleted artifacts, so a deleted artifact opens as "not found"; there is no
 * restore endpoint.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Download, Pencil, Trash2 } from 'lucide-react';
import { Badge, Button, Callout, Chip, ConfirmDialog, Skeleton, useToast } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import {
  deleteArtifactMutation,
  getArtifactOptions,
  getArtifactQueryKey,
  listArtifactsInfiniteQueryKey,
  updateArtifactMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { ArtifactDocBody } from './artifact-detail/ArtifactDocBody.js';
import { ArtifactEditForm } from './artifact-detail/ArtifactEditForm.js';
import { ArtifactRail } from './artifact-detail/ArtifactRail.js';
import { exportAsMarkdown, formatDate, getKindConfig } from './artifact-detail/kind-config.js';

const TABS = [
  { id: 'document', label: 'Document' },
  { id: 'raw', label: 'Source' },
] as const;
const TAB_IDS = TABS.map((t) => t.id);

const LIST_CRUMB = [{ label: 'Artifacts', to: '/artifacts' }];

/** `id` shows that artifact without a route (a conversation's side panel); the route's `:id` otherwise. */
export const ArtifactDetailPage: React.FC<{ id?: string }> = ({ id: idProp }) => {
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const help = PAGE_HELP['artifacts'];
  const [tab, setTab] = useTabParam(TAB_IDS, 'document');
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const query = useQuery(getArtifactOptions({ path: { id } }));
  const artifact = query.data;

  const updateMutation = useMutation({
    ...updateArtifactMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getArtifactQueryKey({ path: { id } }) });
      queryClient.invalidateQueries({ queryKey: listArtifactsInfiniteQueryKey() });
      setEditing(false);
      notify('Artifact updated', 'success');
    },
  });
  const deleteMutation = useMutation({
    ...deleteArtifactMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listArtifactsInfiniteQueryKey() });
      notify('Artifact deleted', 'success');
      navigate('/artifacts');
    },
    onError: (error) => {
      setDeleteOpen(false);
      notify(error.message, 'danger');
    },
  });

  if (query.isPending) {
    return (
      <DetailPage title="Artifact" helpContent={help} breadcrumbs={LIST_CRUMB}>
        <Skeleton height={320} rounded="rounded-lg" />
      </DetailPage>
    );
  }
  if (query.isError || !artifact) {
    return (
      <DetailPage title="Artifact" helpContent={help} breadcrumbs={LIST_CRUMB}>
        <Callout
          tone="danger"
          title={describeQueryError(query.error, 'the artifact')}
          actions={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      </DetailPage>
    );
  }

  const kind = getKindConfig(artifact.kind);
  const version = artifact.metadata?.version ? Number(artifact.metadata.version) : 1;
  const tags = artifact.metadata?.tags ?? [];
  const taskHref = `/tasks/new?originType=artifact&originId=${artifact.id}&originLabel=${encodeURIComponent(artifact.title)}&title=${encodeURIComponent(`Review ${artifact.title}`)}&goal=${encodeURIComponent('Review or refine this artifact and produce a result artifact if changes are needed.')}&inputContext=${encodeURIComponent(JSON.stringify({ artifactId: artifact.id, title: artifact.title, kind: artifact.kind, scope: artifact.scope }))}`;

  return (
    <>
      <DetailPage
        title={artifact.title}
        helpContent={help}
        breadcrumbs={LIST_CRUMB}
        badges={<Badge variant={kind.badgeVariant}>{kind.badgeLabel}</Badge>}
        meta={
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
            <span>Updated {formatDate(artifact.updatedAt)}</span>
            {tags.map((tag) => (
              <Chip key={tag}>{tag}</Chip>
            ))}
          </div>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to={taskHref} className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
              <ClipboardList size={14} aria-hidden="true" />
              Create task
            </Link>
            <Button variant="secondary" size="sm" onClick={() => exportAsMarkdown(artifact)}>
              <Download size={14} aria-hidden="true" />
              Export
            </Button>
            {!editing && (
              <Can policy="CanEditMemoriesArtifacts">
                <Button size="sm" onClick={() => setEditing(true)}>
                  <Pencil size={14} aria-hidden="true" />
                  Edit
                </Button>
              </Can>
            )}
            <Can policy="CanDeleteMemoriesArtifacts">
              <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                <Trash2 size={14} aria-hidden="true" />
                Delete
              </Button>
            </Can>
          </div>
        }
        tabs={editing ? undefined : TABS}
        activeTab={tab}
        onTabChange={setTab}
        rail={
          <ArtifactRail
            kind={artifact.kind}
            scope={artifact.scope}
            subjectType={artifact.subjectType}
            version={version}
            createdAt={artifact.createdAt}
            updatedAt={artifact.updatedAt}
            references={artifact.metadata?.references ?? []}
          />
        }
      >
        {editing ? (
          <ArtifactEditForm
            initial={{ title: artifact.title, content: artifact.content, kind: artifact.kind }}
            onCancel={() => setEditing(false)}
            onSave={(body) => updateMutation.mutateAsync({ path: { id }, body })}
          />
        ) : (
          <ArtifactDocBody content={artifact.content} kind={artifact.kind} raw={tab === 'raw'} />
        )}
      </DetailPage>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate({ path: { id } })}
        title="Delete this artifact?"
        message={`"${artifact.title}" will no longer appear in the library. Restoring it is not possible from the app.`}
        confirmLabel="Delete artifact"
        loading={deleteMutation.isPending}
      />
    </>
  );
};
